import isEqual from 'lodash/isEqual';
import { useCallback } from 'react';
import { getAdditionalParams } from '@/creditflowresearch/application/helpers';
import {
  extractRangeAndBaseFilters,
  getInitialState,
  mapHashPayloadToFilters,
  mapRangesForFilters,
  mapRangesForPayload,
} from '@/creditflowresearch/application/Bonds/BondsFiltersContext/helpers';
import isEmptyString from '@framework/data-helpers/string/isEmptyString';
import { mergeCheckedIdsToParent } from '@framework/ui/Organisms/Filters/helpers';
import Logger from '@framework/sentry/Logger';
import { useCheckboxerContext } from '@framework/ui/Organisms/Filters/Checkboxer/CheckboxerContext';
import { IBondsFilterState } from '@/creditflowresearch/domain/vo/Bond/BondsFiltersContextData';
import { Payload } from '@/creditflowresearch/application/Bonds/BondsFiltersContext/utils';
import { BONDS_STORE_NAME, defaultFiltersPayload } from '@/creditflowresearch/application/Bonds/constants';

const useBondsFilters = ({
  initialFilters,
  setFiltersPayload,
  setInitialFilters,
  setAppliedFilters,
  setMinMaxDatePeriod,
  removeFilterParamsFromUrl,
  updateUrlWithParams,
  getBondsFilter,
  items,
}: {
  initialFilters: IBondsFilterState,
  setFiltersPayload: (payload: any) => void,
  setInitialFilters: (filters: IBondsFilterState) => void,
  setAppliedFilters: (filters: IBondsFilterState) => void,
  setMinMaxDatePeriod: (dateFrom: number, dateTo: number) => void,
  removeFilterParamsFromUrl: () => void,
  updateUrlWithParams: (params: any) => void,
  getBondsFilter: (payload: any) => Promise<any>,
  items: { filter: any, hash?: string },
}) => {
  const { checkboxes } = useCheckboxerContext();

  const getFiltersPayload = (filters: IBondsFilterState = getInitialState()): Payload => {
    const rangeKeys = ['currentFaceRange', 'walRange', 'spreadRange', 'speedRange'];
    const bondsFilter = items.filter || {};

    const [rangeFilters, baseFilters] = extractRangeAndBaseFilters(filters, rangeKeys, bondsFilter);

    const rangePayload = mapRangesForPayload(rangeFilters);

    const { parentIds, childrenIds } = mergeCheckedIdsToParent(
      checkboxes,
      baseFilters,
      'sectorList',
      'includedSubsectorIds',
    );

    return {
      ...defaultFiltersPayload,
      ...baseFilters,
      includedSectorIds: parentIds,
      includedSubsectorIds: childrenIds,
      ...rangePayload,
    };
  };

  const applyFilter = (filters) => {
    const newFiltersPayload = getFiltersPayload(filters);

    const isInitialFilters = isEqual(initialFilters, filters);
    const appliedFilters = isInitialFilters ? {} : filters;
    setFiltersPayload(newFiltersPayload);
    setAppliedFilters(appliedFilters);

    if (isInitialFilters) {
      removeFilterParamsFromUrl();
      return;
    }

    const additionalParams = getAdditionalParams(newFiltersPayload);

    if (Object.keys(additionalParams).length > 0) {
      updateUrlWithParams(additionalParams);
    }
  };

  const fetchFilter = async (filters) => {
    const newFiltersPayload = getFiltersPayload(filters);

    const response = await getBondsFilter(newFiltersPayload);
    const bondsFilter = response.filter || {};
    const isBondsFilter = !!Object.keys(bondsFilter).length;
    const hash = response.hash || '';
    const isInitialFilters = isEqual(initialFilters, filters);
    const isHash = isEmptyString(hash);

    if (isBondsFilter) {
      const {
        dateFrom,
        dateTo,
      } = bondsFilter;
      setMinMaxDatePeriod(dateFrom, dateTo);
    }
    if (!isHash && !isInitialFilters) {
      const additionalParams = getAdditionalParams(newFiltersPayload);
      updateUrlWithParams({ hash, ...additionalParams });
    }
  };

  const initBondsFilters = async (
    filtersPayload: IBondsFilterState = getInitialState(),
    filtersState: IBondsFilterState = getInitialState(),
    isHashPayload: boolean = false,
    filtersUpdate: (storeName: string, filters: IBondsFilterState) => void,
  ) => {
    try {
      const response = await getBondsFilter(filtersPayload);
      const bondsFilter = response.filter;
      const rangesFilters = mapRangesForFilters(bondsFilter);

      setInitialFilters({ ...getInitialState(), ...rangesFilters });
      const hashFilters = mapHashPayloadToFilters(filtersPayload, bondsFilter);

      const filtersToApply = isHashPayload ? hashFilters : { ...filtersState, ...rangesFilters };

      filtersUpdate(BONDS_STORE_NAME, filtersToApply);
      setAppliedFilters(filtersToApply);

      setMinMaxDatePeriod(bondsFilter.dateFrom, bondsFilter.dateTo);
    } catch (error) {
      Logger.warn({ message: 'Failed to set min/max date period for bonds', error });
    }
  };

  const resetFilters = useCallback(async (filtersUpdate) => {
    const initialState = getInitialState();
    const newFiltersPayload = getFiltersPayload(initialState);

    setFiltersPayload(newFiltersPayload);
    await initBondsFilters(newFiltersPayload, initialState, false, filtersUpdate);

    removeFilterParamsFromUrl();
  }, [
    getFiltersPayload,
    setFiltersPayload,
    initBondsFilters,
    removeFilterParamsFromUrl,
  ]);

  return {
    applyFilter,
    fetchFilter,
    initBondsFilters,
    resetFilters,
    getFiltersPayload,
  };
};

export default useBondsFilters;
